import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MapPin, Plus, X, Lock, Unlock, Link2, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { D2DArea } from '@/types/d2d';
import { AreaSharingDialog } from './AreaSharingDialog';
import { AreaFolderDialog } from './AreaFolderDialog';
import { AreaFolderSection } from './AreaFolderSection';
import { Separator } from '@/components/ui/separator';
import { useAreaFolders } from '@/hooks/useAreaFolders';
import { PresenceIndicator } from './PresenceIndicator';
import type { UserPresence } from '@/hooks/useAreaPresence';

interface D2DAreaListProps {
  areas: D2DArea[];
  loading: boolean;
  onSelectArea: (area: D2DArea) => void;
  onAreasUpdated?: () => void;
  onAreaDeactivated?: () => void;
  cacheArea?: (areaId: string) => Promise<void>;
  clearCache?: (areaId: string) => Promise<void>;
  isCaching?: boolean;
  getUsersInArea?: (areaId: string) => UserPresence[];
}

interface AreaSuggestion {
  display_name: string;
  name: string;
  postcode?: string;
  city?: string;
  state?: string;
  osm_id?: string;
  additional?: string;
}

export const D2DAreaList = ({ areas, loading, onSelectArea, onAreasUpdated, onAreaDeactivated, cacheArea, clearCache, isCaching, getUsersInArea }: D2DAreaListProps) => {
  const { user } = useAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', plz: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [localAreas, setLocalAreas] = useState<D2DArea[]>(areas);
  const [unitCounts, setUnitCounts] = useState<Record<string, number>>({});
  const [shareCounts, setShareCounts] = useState<Record<string, number>>({});
  const [sharingAreaId, setSharingAreaId] = useState<string | null>(null);
  const [sharingAreaName, setSharingAreaName] = useState<string>('');
  const [sharedAreaActiveStatus, setSharedAreaActiveStatus] = useState<Record<string, boolean>>({});
  const [activatingAreaId, setActivatingAreaId] = useState<string | null>(null);
  const [tagCounts, setTagCounts] = useState<Record<string, { BK: number; HOT: number; NA: number }>>({});
  
  // Load folder data
  const { 
    folders, 
    createFolder, 
    deleteFolder, 
    toggleFolderExpanded, 
    removeAreaFromFolder,
    areasInFolders 
  } = useAreaFolders();

  // Sync local areas with props - DB is source of truth
  useEffect(() => {
    // Don't sync while an activation is in progress
    if (activatingAreaId) return;
    setLocalAreas(areas);
  }, [areas, activatingAreaId]);

  // Load unit counts for all areas (including shared areas)
  useEffect(() => {
    const loadUnitCounts = async () => {
      if (!user || areas.length === 0) return;

      try {
        const counts: Record<string, number> = {};
        const areaIds = areas.map(area => area.id);
        
        // Get all streets for all areas we can see
        const { data: streetsData } = await supabase
          .from('d2d_streets')
          .select('id, area_id')
          .in('area_id', areaIds);
        
        if (!streetsData || streetsData.length === 0) {
          setUnitCounts({});
          return;
        }
        
        const streetIds = streetsData.map(s => s.id);
        const streetToArea = new Map(streetsData.map(s => [s.id, s.area_id]));
        
        // Get all houses for those streets
        const { data: housesData } = await supabase
          .from('d2d_houses')
          .select('id, street_id')
          .in('street_id', streetIds);
        
        if (!housesData || housesData.length === 0) {
          setUnitCounts({});
          return;
        }
        
        const houseIds = housesData.map(h => h.id);
        const houseToStreet = new Map(housesData.map(h => [h.id, h.street_id]));
        
        // Get all units for those houses (RLS will filter based on permissions)
        const { data: unitsData } = await supabase
          .from('d2d_units')
          .select('id, house_id, tags')
          .in('house_id', houseIds);
        
        // Count units and tags per area
        const tagCountsPerArea: Record<string, { BK: number; HOT: number; NA: number }> = {};
        
        unitsData?.forEach(unit => {
          const streetId = houseToStreet.get(unit.house_id);
          if (streetId) {
            const areaId = streetToArea.get(streetId);
            if (areaId) {
              counts[areaId] = (counts[areaId] || 0) + 1;
              
              // Initialize tag counts for this area if needed
              if (!tagCountsPerArea[areaId]) {
                tagCountsPerArea[areaId] = { BK: 0, HOT: 0, NA: 0 };
              }
              
              // Count tags
              const tags = unit.tags || [];
              if (tags.includes('BK')) tagCountsPerArea[areaId].BK++;
              if (tags.includes('HOT')) tagCountsPerArea[areaId].HOT++;
              if (tags.includes('NA')) tagCountsPerArea[areaId].NA++;
            }
          }
        });

        setUnitCounts(counts);
        setTagCounts(tagCountsPerArea);
      } catch (error) {
        console.error('Error loading unit counts:', error);
      }
    };

    loadUnitCounts();
  }, [areas, user]);

  // Load share counts for all areas
  useEffect(() => {
    loadShareCounts();
    loadSharedAreaActiveStatus();
  }, [areas, user]);

  const loadShareCounts = async () => {
    if (!user || areas.length === 0) return;

    try {
      const areaIds = areas.map(area => area.id);

      // Count shares for each area
      const { data, error } = await supabase
        .from('d2d_area_shares')
        .select('area_id')
        .in('area_id', areaIds);

      if (error) throw error;

      // Count shares per area
      const counts: Record<string, number> = {};
      data?.forEach(share => {
        counts[share.area_id] = (counts[share.area_id] || 0) + 1;
      });

      setShareCounts(counts);
    } catch (error) {
      console.error('Error loading share counts:', error);
    }
  };

  // Load active status for shared areas (areas shared WITH this user)
  const loadSharedAreaActiveStatus = async () => {
    if (!user || areas.length === 0) return;

    try {
      // Get shares where this user is the recipient
      const { data, error } = await supabase
        .from('d2d_area_shares')
        .select('area_id, is_active')
        .eq('shared_with', user.id);

      if (error) throw error;

      // Build status map
      const statusMap: Record<string, boolean> = {};
      data?.forEach(share => {
        statusMap[share.area_id] = share.is_active ?? true;
      });

      setSharedAreaActiveStatus(statusMap);
    } catch (error) {
      console.error('Error loading shared area active status:', error);
    }
  };

  // Toggle shared area active status (for areas shared with the user)
  const handleToggleSharedAreaActive = async (areaId: string, currentActive: boolean) => {
    if (!user || activatingAreaId) return;
    
    // Show loading FIRST - this also blocks the sync useEffect
    setActivatingAreaId(areaId);
    
    try {
      // Run all DB operations in sequence
      if (!currentActive) {
        // Deactivate all own areas in DB
        const { error: ownAreasError } = await supabase
          .from('d2d_areas')
          .update({ is_active: false })
          .eq('created_by', user.id);
        
        if (ownAreasError) throw ownAreasError;
        
        // Deactivate all other shared areas
        const { error: otherSharesError } = await supabase
          .from('d2d_area_shares')
          .update({ is_active: false })
          .eq('shared_with', user.id)
          .neq('area_id', areaId);
        
        if (otherSharesError) throw otherSharesError;
      } else {
        // Clear cache when deactivating
        if (clearCache) {
          await clearCache(areaId);
        }
      }

      // Toggle this shared area
      const { error } = await supabase
        .from('d2d_area_shares')
        .update({ is_active: !currentActive })
        .eq('area_id', areaId)
        .eq('shared_with', user.id);

      if (error) throw error;

      // Update local state AFTER all DB operations succeed
      if (!currentActive) {
        setLocalAreas(prev => prev.map(area => 
          area.created_by === user.id ? { ...area, is_active: false } : area
        ));
        setSharedAreaActiveStatus(prev => {
          const newStatus: Record<string, boolean> = {};
          Object.keys(prev).forEach(id => {
            newStatus[id] = id === areaId;
          });
          newStatus[areaId] = true;
          return newStatus;
        });
        
        // Cache the area for offline use AFTER state is updated
        if (cacheArea) {
          cacheArea(areaId);
        }
      } else {
        setSharedAreaActiveStatus(prev => ({
          ...prev,
          [areaId]: false
        }));
        // Notify parent to reset navigation when deactivating
        if (onAreaDeactivated) {
          onAreaDeactivated();
        }
      }

      // Refresh areas from parent to ensure consistency
      onAreasUpdated?.();

      toast.success(
        !currentActive
          ? 'Geteiltes Gebiet aktiviert'
          : 'Geteiltes Gebiet deaktiviert'
      );
    } catch (error) {
      console.error('Error toggling shared area active status:', error);
      toast.error('Fehler beim Ändern des Status');
    } finally {
      setActivatingAreaId(null);
    }
  };

  // Subscribe to unit changes for real-time updates
  useEffect(() => {
    if (!user || areas.length === 0) return;

    const channel = supabase
      .channel('unit-counts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'd2d_units'
        },
        () => {
          // Reload unit counts when units change
          const loadUnitCounts = async () => {
            if (areas.length === 0) return;

            try {
              const areaIds = areas.map(area => area.id);

              const { data: streetsData, error: streetsError } = await supabase
                .from('d2d_streets')
                .select('id, area_id')
                .in('area_id', areaIds);

              if (streetsError) throw streetsError;

              const streetIds = streetsData?.map(s => s.id) || [];
              if (streetIds.length === 0) {
                setUnitCounts({});
                return;
              }

              const { data: housesData, error: housesError } = await supabase
                .from('d2d_houses')
                .select('id, street_id')
                .in('street_id', streetIds);

              if (housesError) throw housesError;

              const houseIds = housesData?.map(h => h.id) || [];
              if (houseIds.length === 0) {
                setUnitCounts({});
                return;
              }

              const { data, error } = await supabase
                .from('d2d_units')
                .select('id, house_id')
                .in('house_id', houseIds);

              if (error) throw error;

              const houseToStreet = new Map(housesData?.map(h => [h.id, h.street_id]) || []);
              const streetToArea = new Map(streetsData?.map(s => [s.id, s.area_id]) || []);

              const counts: Record<string, number> = {};
              data?.forEach(unit => {
                const streetId = houseToStreet.get(unit.house_id);
                if (streetId) {
                  const areaId = streetToArea.get(streetId);
                  if (areaId) {
                    counts[areaId] = (counts[areaId] || 0) + 1;
                  }
                }
              });

              setUnitCounts(counts);
            } catch (error) {
              console.error('Error reloading unit counts:', error);
            }
          };

          loadUnitCounts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [areas, user]);

  // Subscribe to area share changes for real-time updates
  useEffect(() => {
    if (!user || areas.length === 0) return;

    const channel = supabase
      .channel('area-shares-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'd2d_area_shares'
        },
        () => {
          // Reload share counts when shares change
          const loadShareCounts = async () => {
            if (areas.length === 0) return;

            try {
              const areaIds = areas.map(area => area.id);

              const { data, error } = await supabase
                .from('d2d_area_shares')
                .select('area_id')
                .in('area_id', areaIds);

              if (error) throw error;

              const counts: Record<string, number> = {};
              data?.forEach(share => {
                counts[share.area_id] = (counts[share.area_id] || 0) + 1;
              });

              setShareCounts(counts);
            } catch (error) {
              console.error('Error reloading share counts:', error);
            }
          };

          loadShareCounts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [areas, user]);

  // Manuelle Eingabe - keine API-Suche mehr

  // Subscribe to d2d_areas changes for real-time updates (INSERT, DELETE only)
  // UPDATE events are handled via optimistic updates to avoid race conditions
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('areas-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'd2d_areas'
        },
        () => {
          // Reload areas when new areas are created
          onAreasUpdated?.();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'd2d_areas'
        },
        () => {
          // Reload areas when areas are deleted
          onAreasUpdated?.();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, onAreasUpdated]);

  const handleCreateArea = async () => {
    if (!formData.plz.trim() || !user || isCreating) return;

    setIsCreating(true);
    try {
      // Get user profile for organization_id (still needed for database structure)
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      const { data, error } = await supabase
        .from('d2d_areas')
        .insert({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          plz: formData.plz.trim() || null,
          organization_id: profile?.organization_id || user.id, // Use user.id as fallback
          created_by: user.id
        });

      if (error) {
        console.error('Database error creating area:', error);
        throw new Error(`Fehler beim Erstellen des Gebiets: ${error.message}`);
      }

      toast.success('Gebiet erfolgreich erstellt');
      
      // Reset form
      setFormData({ name: '', description: '', plz: '' });
      setIsCreateDialogOpen(false);
      
      // Safely call onAreasUpdated with delay to prevent race conditions
      setTimeout(() => {
        try {
          onAreasUpdated?.();
        } catch (callbackError) {
          console.error('Error in onAreasUpdated callback:', callbackError);
        }
      }, 100);
      
    } catch (error) {
      console.error('Error creating area:', error);
      
      // Enhanced error handling
      const errorMessage = error instanceof Error ? error.message : 'Ein unerwarteter Fehler ist aufgetreten';
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteArea = async (areaId: string, areaName: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase.rpc('delete_area_cascade', {
        area_id_param: areaId,
        user_id_param: user.id
      });

      if (error) throw error;

      toast.success(`Gebiet "${areaName}" und alle zugehörigen Daten erfolgreich gelöscht`);
      onAreasUpdated?.();
    } catch (error) {
      console.error('Error deleting area:', error);
      toast.error('Fehler beim Löschen des Gebiets');
    }
  };

  const handleToggleAreaActive = async (areaId: string, currentActive: boolean) => {
    if (!user || activatingAreaId) return;
    
    // Show loading FIRST - this also blocks the sync useEffect
    setActivatingAreaId(areaId);
    
    try {
      // Run all DB operations in sequence
      if (!currentActive) {
        // Deactivate all other own areas
        const { error: otherAreasError } = await supabase
          .from('d2d_areas')
          .update({ is_active: false })
          .eq('created_by', user.id)
          .neq('id', areaId);
        
        if (otherAreasError) throw otherAreasError;
        
        // Deactivate all shared areas
        const { error: sharesError } = await supabase
          .from('d2d_area_shares')
          .update({ is_active: false })
          .eq('shared_with', user.id);
        
        if (sharesError) throw sharesError;
      } else {
        // Clear cache when deactivating
        if (clearCache) {
          await clearCache(areaId);
        }
      }

      // Toggle this own area
      const { error } = await supabase
        .from('d2d_areas')
        .update({ is_active: !currentActive })
        .eq('id', areaId)
        .eq('created_by', user.id);

      if (error) throw error;

      // Update local state AFTER all DB operations succeed
      if (!currentActive) {
        setLocalAreas(prev => prev.map(area => {
          if (area.created_by !== user.id) return area;
          return { ...area, is_active: area.id === areaId };
        }));
        setSharedAreaActiveStatus(prev => {
          const newStatus: Record<string, boolean> = {};
          Object.keys(prev).forEach(id => {
            newStatus[id] = false;
          });
          return newStatus;
        });
        
        // Cache the area for offline use AFTER state is updated
        if (cacheArea) {
          cacheArea(areaId);
        }
      } else {
        setLocalAreas(prev => prev.map(area => 
          area.id === areaId ? { ...area, is_active: false } : area
        ));
        // Notify parent to reset navigation when deactivating
        if (onAreaDeactivated) {
          onAreaDeactivated();
        }
      }

      // Refresh areas from parent to ensure consistency
      onAreasUpdated?.();

      toast.success(
        !currentActive 
          ? 'Gebiet aktiviert' 
          : 'Gebiet deaktiviert'
      );
    } catch (error) {
      console.error('Error toggling area active status:', error);
      toast.error('Fehler beim Ändern des Gebietsstatus');
    } finally {
      setActivatingAreaId(null);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (localAreas.length === 0) {
    return (
      <div>
        <div className="text-center p-8">
          <MapPin className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">Noch keine Gebiete</h3>
          <p className="text-muted-foreground mb-6">
            Verwenden Sie den Button unten, um Ihr erstes Gebiet zu erstellen.
          </p>
          
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Neues Gebiet
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Neues Gebiet erstellen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="area-name-empty">Gebietsname</Label>
                  <Input
                    id="area-name-empty"
                    placeholder="z.B. Mühlburg"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="area-plz-empty">PLZ *</Label>
                  <Input
                    id="area-plz-empty"
                    placeholder="z.B. 76185"
                    value={formData.plz}
                    onChange={(e) => setFormData(prev => ({ ...prev, plz: e.target.value.replace(/\D/g, '') }))}
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="area-description-empty">Beschreibung (optional)</Label>
                  <Textarea
                    id="area-description-empty"
                    placeholder="Kurze Beschreibung des Gebiets..."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                <Button 
                  onClick={handleCreateArea} 
                  disabled={!formData.plz.trim() || isCreating}
                  className="flex-1"
                >
                    {isCreating ? "Wird erstellt..." : "Gebiet erstellen"}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsCreateDialogOpen(false)}
                    disabled={isCreating}
                  >
                    Abbrechen
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  const handleCreateFolderWrapper = async (folderName: string, areaIds: string[]) => {
    await createFolder(folderName, areaIds);
  };

  // Helper function to activate area and navigate
  const handleActivateAndNavigate = async (area: D2DArea) => {
    const isOwnArea = area.created_by === user?.id;
    const isActive = isOwnArea ? area.is_active : (sharedAreaActiveStatus[area.id] !== false);
    
    // If area is not active, activate it first
    if (!isActive && user) {
      setActivatingAreaId(area.id);
      
      try {
        if (isOwnArea) {
          await supabase.from('d2d_areas').update({ is_active: false }).eq('created_by', user.id).neq('id', area.id);
          await supabase.from('d2d_area_shares').update({ is_active: false }).eq('shared_with', user.id);
          await supabase.from('d2d_areas').update({ is_active: true }).eq('id', area.id).eq('created_by', user.id);
        } else {
          await supabase.from('d2d_areas').update({ is_active: false }).eq('created_by', user.id);
          await supabase.from('d2d_area_shares').update({ is_active: false }).eq('shared_with', user.id).neq('area_id', area.id);
          await supabase.from('d2d_area_shares').update({ is_active: true }).eq('area_id', area.id).eq('shared_with', user.id);
        }
        if (cacheArea) cacheArea(area.id);
        onAreasUpdated?.();
      } catch (error) {
        console.error('Error activating area:', error);
      } finally {
        setActivatingAreaId(null);
      }
    }
    
    // Navigate
    const activeArea = { ...area, is_active: true };
    onSelectArea(activeArea);
  };

  // Filter areas that are NOT in any folder
  const unfolderAreas = localAreas.filter(area => !areasInFolders.includes(area.id));

  return (
    <div>
      <div className="mt-3 mb-6 space-y-3">
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 w-full">
              <Plus className="h-4 w-4" />
              Neues Gebiet erstellen
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Neues Gebiet erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="area-name">Gebietsname</Label>
                <Input
                  id="area-name"
                  placeholder="z.B. Mühlburg"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="area-plz">PLZ *</Label>
                <Input
                  id="area-plz"
                  placeholder="z.B. 76185"
                  value={formData.plz}
                  onChange={(e) => setFormData(prev => ({ ...prev, plz: e.target.value.replace(/\D/g, '') }))}
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="area-description">Beschreibung (optional)</Label>
                <Textarea
                  id="area-description"
                  placeholder="Kurze Beschreibung des Gebiets..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
              <Button 
                onClick={handleCreateArea} 
                disabled={!formData.plz.trim() || isCreating}
                className="flex-1"
              >
                  {isCreating ? "Wird erstellt..." : "Gebiet erstellen"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setIsCreateDialogOpen(false)}
                  disabled={isCreating}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        <Separator />
        
        <AreaFolderDialog 
          areas={localAreas} 
          onCreateFolder={handleCreateFolderWrapper}
        />
      </div>

      {/* Folders Section */}
      {folders.length > 0 && (
        <div className="space-y-4 mb-6">
          {folders.map(folder => (
            <AreaFolderSection
              key={folder.id}
              folder={folder}
              areas={localAreas}
              unitCounts={unitCounts}
              tagCounts={tagCounts}
              sharedAreaActiveStatus={sharedAreaActiveStatus}
              shareCounts={shareCounts}
              activatingAreaId={activatingAreaId}
              userId={user?.id}
              onSelectArea={onSelectArea}
              onToggleExpanded={toggleFolderExpanded}
              onDeleteFolder={async (folderId, deleteAreas) => {
                const folder = folders.find(f => f.id === folderId);
                const deletedAreaIds = deleteAreas && folder ? folder.area_ids : [];
                
                await deleteFolder(folderId, deleteAreas);
                
                if (deleteAreas && deletedAreaIds.length > 0) {
                  // Update local areas state immediately
                  setLocalAreas(prev => prev.filter(a => !deletedAreaIds.includes(a.id)));
                  onAreasUpdated?.();
                }
              }}
              onRemoveArea={removeAreaFromFolder}
              onToggleAreaActive={handleToggleAreaActive}
              onToggleSharedAreaActive={handleToggleSharedAreaActive}
              onActivateAndNavigate={handleActivateAndNavigate}
              onOpenSharingDialog={(areaId, areaName) => {
                setSharingAreaId(areaId);
                setSharingAreaName(areaName);
              }}
            />
          ))}
          
          {unfolderAreas.length > 0 && (
            <Separator className="my-4" />
          )}
        </div>
      )}
      
      {/* Unfolderd Areas */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Sort areas: active first, then by name */}
        {[...unfolderAreas].sort((a, b) => {
          // Check active status for own vs shared areas
          const aIsActive = a.created_by === user?.id ? a.is_active : (sharedAreaActiveStatus[a.id] !== false);
          const bIsActive = b.created_by === user?.id ? b.is_active : (sharedAreaActiveStatus[b.id] !== false);
          
          // Active areas first
          if (aIsActive && !bIsActive) return -1;
          if (!aIsActive && bIsActive) return 1;
          
          // Then sort by name
          return a.name.localeCompare(b.name);
        }).map((area) => (
          <Card 
            key={area.id} 
            className={`relative hover:shadow-md transition-all ${
              area.created_by === user?.id 
                ? (!area.is_active ? 'opacity-50' : '')
                : (sharedAreaActiveStatus[area.id] === false ? 'opacity-50' : '')
            }`}
          >
            <div 
              className="cursor-pointer"
              onClick={async (e) => {
                e.preventDefault();
                
                const isOwnArea = area.created_by === user?.id;
                const isActive = isOwnArea ? area.is_active : (sharedAreaActiveStatus[area.id] !== false);
                
                // If area is not active, activate it first
                if (!isActive && user) {
                  setActivatingAreaId(area.id);
                  
                  try {
                    if (isOwnArea) {
                      await supabase.from('d2d_areas').update({ is_active: false }).eq('created_by', user.id).neq('id', area.id);
                      await supabase.from('d2d_area_shares').update({ is_active: false }).eq('shared_with', user.id);
                      await supabase.from('d2d_areas').update({ is_active: true }).eq('id', area.id).eq('created_by', user.id);
                    } else {
                      await supabase.from('d2d_areas').update({ is_active: false }).eq('created_by', user.id);
                      await supabase.from('d2d_area_shares').update({ is_active: false }).eq('shared_with', user.id).neq('area_id', area.id);
                      await supabase.from('d2d_area_shares').update({ is_active: true }).eq('area_id', area.id).eq('shared_with', user.id);
                    }
                    if (cacheArea) cacheArea(area.id);
                    onAreasUpdated?.();
                  } catch (error) {
                    console.error('Error activating area:', error);
                  } finally {
                    setActivatingAreaId(null);
                  }
                }
                
                // Create area copy with is_active: true for navigation
                const activeArea = { ...area, is_active: true };
                
                // Navigate AFTER DB is updated
                onSelectArea(activeArea);
              }}
            >
               <CardHeader className="pb-3">
                  <div className="absolute top-2 left-2 flex gap-1">
                    {/* Toggle button - different behavior for own vs shared areas */}
                    {area.created_by === user?.id ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-transparent"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleAreaActive(area.id, area.is_active);
                        }}
                        title={area.is_active ? 'Gebiet deaktivieren' : 'Gebiet aktivieren'}
                        disabled={activatingAreaId === area.id}
                      >
                        {activatingAreaId === area.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : area.is_active ? (
                          <Unlock className="h-4 w-4 text-green-600" />
                        ) : (
                          <Lock className="h-4 w-4 text-red-600" />
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-transparent"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleSharedAreaActive(area.id, sharedAreaActiveStatus[area.id] !== false);
                        }}
                        title={sharedAreaActiveStatus[area.id] !== false ? 'Geteiltes Gebiet ausblenden' : 'Geteiltes Gebiet einblenden'}
                        disabled={activatingAreaId === area.id}
                      >
                        {activatingAreaId === area.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : sharedAreaActiveStatus[area.id] !== false ? (
                          <Unlock className="h-4 w-4 text-blue-500" />
                        ) : (
                          <Lock className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSharingAreaId(area.id);
                        setSharingAreaName(area.name);
                      }}
                      title={area.created_by !== user?.id ? "Mit dir geteilt" : "Gebiet mit Teammitgliedern teilen"}
                    >
                      {area.created_by !== user?.id ? (
                        <Users className="h-4 w-4 text-blue-500" />
                      ) : shareCounts[area.id] > 0 ? (
                        <Link2 className="h-4 w-4 text-blue-500" />
                      ) : (
                        <Link2 className="h-4 w-4 text-primary" />
                      )}
                    </Button>
                  </div>
                  
                  <div className="flex flex-col items-center justify-center">
                    <MapPin className="h-5 w-5 text-primary mb-2" />
                    <div className="text-center">
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                      {area.plz && <p className="text-sm text-muted-foreground">{area.plz}</p>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {unitCounts[area.id] || 0} {unitCounts[area.id] === 1 ? 'Wohneinheit' : 'Wohneinheiten'}
                    </p>
                    {/* Presence indicator - who's working in this area */}
                    {getUsersInArea && getUsersInArea(area.id).length > 0 && (
                      <div className="mt-2">
                        <PresenceIndicator users={getUsersInArea(area.id)} />
                      </div>
                    )}
                  </div>
                  
                 {area.description && !area.description.includes('Migriert aus altem System') && (
                   <CardDescription className="text-sm text-center mt-2">
                     {area.description}
                   </CardDescription>
                 )}
               </CardHeader>
              <CardContent className="pt-0 pb-2">
                <div className="flex justify-center gap-1.5">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600">
                    BK {tagCounts[area.id]?.BK || 0}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600">
                    HOT {tagCounts[area.id]?.HOT || 0}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-600">
                    NA {tagCounts[area.id]?.NA || 0}
                  </span>
                </div>
              </CardContent>
            </div>
            
            {/* Only show delete button for areas created by the user */}
            {area.created_by === user?.id && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Gebiet löschen</AlertDialogTitle>
                    <AlertDialogDescription>
                      Sind Sie sicher, dass Sie das Gebiet "{area.name}" löschen möchten? 
                      Diese Aktion kann nicht rückgängig gemacht werden und alle zugehörigen Straßen, Häuser und Einheiten werden ebenfalls gelöscht.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => handleDeleteArea(area.id, area.name)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Löschen
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </Card>
        ))}
      </div>

      <AreaSharingDialog
        isOpen={sharingAreaId !== null}
        onClose={() => {
          setSharingAreaId(null);
          setSharingAreaName('');
        }}
        areaId={sharingAreaId || ''}
        areaName={sharingAreaName}
        onSharesUpdated={loadShareCounts}
      />
    </div>
  );
};